<template>
  <div class="vehicle">
    <h2 class="h4">Welches Fahrzeug sollen wir uns ansehen?</h2>
    <el-radio-group
      v-model="selectedVehicleId"
      v-if="hasUserVehicles"
      @change="handleVehicleSelectChange($event)"
    >
      <Selection
        v-for="(vehicle, index) in userVehicles"
        :key="vehicle.vehicle.kfzartId"
        :title="
          vehicle.vehicle.brand +
          ' ' +
          (vehicle.vehicle.modelDescription || vehicle.vehicle.model) +
          ' &#8226; ' +
          (vehicle.vehicle.licensePlate ? vehicle.vehicle.licensePlate : 'Kennzeichen unbekannt')
        "
        :subtitle="
          isUserVehicleDisabled(vehicle.vehicle.kfzartId)
            ? 'Die gewählte Dienstleistung ist für dieses Fahrzeug leider nicht verfügbar'
            : 'Von ' +
              vehicle.vehicle.erstzulassung.slice(0, 4) +
              ' - ' +
              vehicle.vehicle.antriebsart
        "
        :info="showInitialRegistrationText ? initialRegistrationText : ''"
        :active="vehicle.vehicle.id === selectedVehicleId"
        :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
        :divider="index !== userVehicles.length - 1"
        @click="clickVehicleHandler(vehicle.vehicle.id, vehicle.vehicle.kfzartId)"
      >
        <template v-slot:icon>
          <IconVehicleCar
            aria-hidden="true"
            v-if="vehicle.vehicle.category.toLowerCase().includes('pkw')"
            :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
          />
          <IconVehicleTruck
            aria-hidden="true"
            v-else-if="vehicle.vehicle.category.toLowerCase().includes('lkw')"
            :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
          />
          <IconVehicleMotorcycle
            aria-hidden="true"
            v-else-if="isMotorrad(vehicle.vehicle.category)"
            :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
          />
          <!-- TODO: Fahrrad-Icon hinzufügen falls Fahrräder in Mein ÖAMTC gespeichert werden können -->
          <IconVehicleTrailer
            aria-hidden="true"
            v-else
            :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
          />
        </template>
        <template v-slot:action>
          <el-radio
            :label="
              vehicle.vehicle.brand +
              ' ' +
              (vehicle.vehicle.modelDescription || vehicle.vehicle.model) +
              ' &#8226; ' +
              (vehicle.vehicle.licensePlate
                ? vehicle.vehicle.licensePlate
                : 'Kennzeichen unbekannt')
            "
            :value="vehicle.vehicle.id"
            :disabled="isUserVehicleDisabled(vehicle.vehicle.kfzartId)"
            @click.prevent
          />
        </template>
      </Selection>
    </el-radio-group>
    <Selection
      v-if="hasNoUserVehicles"
      title="Kein gespeichertes Fahrzeug in Ihrem Mein ÖAMTC Account"
      :subtitle="noVehicleSubtitle"
      :divider="false"
      disabled
    >
      <template v-slot:icon>
        <IconVehicleCar aria-hidden="true" disabled />
      </template>
    </Selection>
    <hr />
    <div class="vehicle__new">
      <div class="vehicle__header">
        <div class="vehicle__header__subheadline">
          <h2>
            {{ hasUserVehicles ? 'Nicht das richtige Fahrzeug dabei?' : 'Neues Fahrzeug erfassen' }}
          </h2>
          <p>Alle benötigten Daten finden Sie in Ihrem Zulassungsschein</p>
        </div>
        <button
          v-if="hasUserVehicles && !showForm"
          class="vehicle__header__action link"
          @click="toggleShowForm"
        >
          <IconCirclePlus aria-hidden="true" />Fahrzeug erfassen
        </button>
        <button
          v-if="hasUserVehicles && showForm"
          class="vehicle__header__action link"
          @click="toggleShowForm"
        >
          <IconCircleCross aria-hidden="true" />Verwerfen
        </button>
      </div>
      <div class="vehicle__form" v-show="showForm">
        <el-form
          label-position="top"
          :inline="true"
          :model="form"
          :rules="$options.rules"
          ref="elForm"
        >
          <el-radio-group v-model="form.type">
            <el-radio-button label="PKW" />
            <el-radio-button label="Motorrad" />
            <el-radio-button label="Fahrrad" />
            <el-radio-button label="LKW" />
            <el-radio-button label="Weitere" />
          </el-radio-group>
          <div class="row" v-if="!kaufException && !isBicycle">
            <el-form-item prop="fin" :class="{ 'is-error': error.fin }">
              <template v-slot:label>
                Fahrzeug-Identifikationsnummer (FIN) · E
                <VehicleInfo type="fin" />
              </template>
              <el-input
                ref="elFormFin"
                v-model="form.fin"
                v-mask="{ mask: '*** ****** ********' }"
                placeholder="XXX XXXXXX XXXXXXXX"
                maxlength="40"
                @change="validateFin"
              />
            </el-form-item>
            <div class="vehicle__form__error" v-if="error.fin">
              {{ error.fin }}
            </div>
            <div class="message" v-show="form.fin && !error.fin">
              <IconCircleExclamation aria-hidden="true" />
              Durch die FIN werden einige Felder automatisch ausgefüllt, bitte kontrollieren Sie
              alle Daten.
            </div>
          </div>
          <el-form-item prop="licensePlate">
            <template v-slot:label>
              Kennzeichen · A
              <VehicleInfo type="licensePlate" />
            </template>
            <el-input
              v-model="form.licensePlate"
              placeholder="XX-XXXX"
              maxlength="40"
              :disabled="kaufException || isBicycle"
            />
          </el-form-item>
          <el-form-item prop="initialRegistration">
            <template v-slot:label>
              Erstzulassung · B
              <VehicleInfo type="initialRegistration" />
            </template>
            <el-input
              v-model="form.initialRegistration"
              placeholder="TT.MM.JJJJ"
              v-mask="{ mask: '99.99.9999' }"
              maxlength="40"
              :disabled="isBicycle"
            >
              <template #suffix>
                <IconCalendar aria-hidden="true" class="el-input__icon" color="#737380" size="20" />
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="lastName">
            <template v-slot:label>
              Zulassungsbesitzer Nachname · C1.2
              <VehicleInfo type="lastName" />
            </template>
            <el-input v-model="form.lastName" maxlength="40" />
          </el-form-item>
          <el-form-item prop="firstName">
            <template v-slot:label>
              Zulassungsbesitzer Vorname · C1.2
              <VehicleInfo type="firstName" />
            </template>
            <el-input v-model="form.firstName" maxlength="40" />
          </el-form-item>
          <el-form-item prop="country">
            <template v-slot:label> Zulassungsland </template>
            <el-select
              ref="countrySelect"
              v-model="form.country"
              filterable
              placeholder="Bitte wählen"
              :teleported="false"
              :fit-input-width="true"
              class="z2009"
              @change="selectChangeHandler('countrySelect')"
            >
              <el-option
                v-for="country in countries"
                :key="country.key"
                :label="country.value"
                :value="country.key"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item prop="brand">
            <template v-slot:label>
              Marke · D1 <span class="required">(Pflichtfeld)</span>
              <VehicleInfo type="brand" />
            </template>
            <el-select
              ref="brandSelect"
              v-model="form.brand"
              filterable
              :filter-method="filterBrands"
              @visible-change="resetBrands"
              placeholder="Bitte wählen"
              :teleported="false"
              :fit-input-width="true"
              class="z2007"
              @change="selectChangeHandler('brandSelect')"
              @blur="handleSelectBlur('brand', 'brandSelect')"
            >
              <el-option v-for="brand in filteredBrands" :key="brand" :label="brand" :value="brand">
              </el-option>
            </el-select>
            <template v-slot:error="{ error }">
              <div :id="errorIds.brandSelect" class="el-form-item__error">{{ error }}</div>
            </template>
          </el-form-item>
          <el-form-item prop="model">
            <template v-slot:label>
              Handelsbezeichnung · D3 <span class="required">(Pflichtfeld)</span>
              <VehicleInfo type="model" />
            </template>
            <el-select
              ref="modelSelect"
              v-model="form.model"
              filterable
              placeholder="Bitte wählen"
              :teleported="false"
              :fit-input-width="true"
              no-data-text="Keine Daten"
              class="z2005"
              @change="selectChangeHandler('modelSelect')"
              @blur="handleSelectBlur('model', 'modelSelect')"
            >
              <el-option
                v-for="model in vehicleBrandsModels[form.brand]"
                :key="model"
                :label="model"
                :value="model"
              >
              </el-option>
              <template v-slot:empty>
                <div class="el-select-dropdown__empty">
                  <IconVehicleCar aria-hidden="true" color="#737380" />
                  Bitte zuerst Marke auswählen
                </div>
              </template>
            </el-select>
            <template v-slot:error="{ error }">
              <div :id="errorIds.modelSelect" class="el-form-item__error">{{ error }}</div>
            </template>
          </el-form-item>
          <el-form-item prop="category">
            <template v-slot:label>
              Klasse/Fahrzeugart · J <span class="required">(Pflichtfeld)</span>
              <VehicleInfo type="category" />
            </template>
            <el-select
              ref="categorySelect"
              v-model="form.category"
              filterable
              placeholder="Bitte wählen"
              :teleported="false"
              :fit-input-width="true"
              class="z2003"
              @change="selectChangeHandler('categorySelect')"
              @blur="handleSelectBlur('category', 'categorySelect')"
            >
              <el-option
                v-for="category in filteredCategories?.map((el) => el.title)"
                :key="category"
                :label="category"
                :value="category"
              >
              </el-option>
            </el-select>
            <template v-slot:error="{ error }">
              <div :id="errorIds.categorySelect" class="el-form-item__error">{{ error }}</div>
            </template>
          </el-form-item>
          <el-form-item prop="engine">
            <template v-slot:label>
              Antrieb · P3 <span class="required">(Pflichtfeld)</span>
              <VehicleInfo type="engine" />
            </template>
            <el-select
              ref="engineSelect"
              v-model="form.engine"
              filterable
              placeholder="Bitte wählen"
              :teleported="false"
              :fit-input-width="true"
              class="z2001"
              @change="selectChangeHandler('engineSelect')"
              @blur="handleSelectBlur('engine', 'engineSelect')"
            >
              <el-option
                v-for="drives in filteredVehicleDrives"
                :key="drives"
                :label="drives"
                :value="drives"
              >
              </el-option>
            </el-select>
            <template v-slot:error="{ error }">
              <div :id="errorIds.engineSelect" class="el-form-item__error">{{ error }}</div>
            </template>
          </el-form-item>
          <div v-if="form.type === 'PKW'" class="row">
            <el-form-item prop="fourWheelDrive">
              <el-checkbox v-model="form.fourWheelDrive" label="Mein Fahrzeug hat Allrad" />
            </el-form-item>
          </div>
        </el-form>
        <p v-if="showInitialRegistrationText">{{ initialRegistrationText }}</p>
      </div>
    </div>
    <Navigation
      :isNextButtonDisabled="!(selectedVehicleId || (hasCompletedForm && isFormValid))"
      @nextStep="$event === 2 && nextStep()"
    />
  </div>
</template>

<script>
import { getDateYearsAgoToday, removeDuplicates } from '@/utils/format.js'
import { trackGa4Event } from '@/utils/tracking'
import { mapGetters } from 'vuex'

import IconCalendar from '@/components/icons/IconCalendar.vue'
import IconCircleCross from '@/components/icons/IconCircleCross.vue'
import IconCircleExclamation from '@/components/icons/IconCircleExclamation.vue'
import IconCirclePlus from '@/components/icons/IconCirclePlus.vue'
import IconVehicleCar from '@/components/icons/IconVehicleCar.vue'
import IconVehicleMotorcycle from '@/components/icons/IconVehicleMotorcycle.vue'
import IconVehicleTrailer from '@/components/icons/IconVehicleTrailer.vue'
import IconVehicleTruck from '@/components/icons/IconVehicleTruck.vue'

import Navigation from '@/components/Booking/Navigation.vue'
import VehicleInfo from '@/components/Booking/VehicleInfo.vue'
import Selection from '@/components/Selection.vue'

export default {
  components: {
    IconCalendar,
    IconCircleCross,
    IconCircleExclamation,
    IconCirclePlus,
    IconVehicleCar,
    IconVehicleMotorcycle,
    IconVehicleTruck,
    IconVehicleTrailer,
    Navigation,
    VehicleInfo,
    Selection,
  },
  rules: {
    fin: [
      {
        validator: (rule, value, callback) => {
          const newValue = value?.replaceAll('_', '').replaceAll(' ', '')
          if (
            newValue &&
            ['I', 'i', 'O', 'o', 'Q', 'q'].some(
              (alphabet) => newValue.includes(alphabet) || newValue.length > 17
            )
          ) {
            callback(new Error('max. 17 Zeichen und keine Buchstaben Q, O oder I'))
          } else {
            callback()
          }
        },
        trigger: 'blur',
      },
    ],
    licensePlate: [
      {
        validator: (rule, value, callback) => {
          if (value.length > 0 && !value.match('[a-zA-Z]+-[a-zA-Z0-9]+')) {
            callback(new Error('Ungültige Eingabe'))
          } else {
            callback()
          }
        },
        trigger: 'blur',
      },
    ],
    initialRegistration: [
      {
        validator: (rule, value, callback) => {
          const numberValue = value.replaceAll('_', '').replaceAll('.', '')
          const dateValue = new Date(value.split('.').reverse().join('-'))

          if (numberValue === '') {
            callback() // optional
          } else if (numberValue.length > 0 && numberValue.length < 8) {
            callback(new Error('Unvollständige Eingabe'))
          } else if (
            isNaN(dateValue) ||
            dateValue > new Date() ||
            dateValue < new Date('1900-01-01')
          ) {
            callback(new Error('Ungültige Eingabe'))
          } else {
            callback()
          }
        },
        trigger: 'blur',
      },
    ],
    brand: [{ required: true, message: 'Pflichtfeld', trigger: 'blur' }],
    model: [{ required: true, message: 'Pflichtfeld', trigger: 'blur' }],
    category: [{ required: true, message: 'Pflichtfeld', trigger: 'blur' }],
    engine: [{ required: true, message: 'Pflichtfeld', trigger: 'blur' }],
  },
  data() {
    return {
      noVehicleSubtitle: `Nach dem Anlegen in <a class="link" target="_blank" href="${window.location.origin}/mein-oeamtc/meine-fahrzeuge">"Meine Fahrzeuge"</a> finden sie Ihre Fahrzeuge hier`,
      filteredBrands: [],
      selectedVehicleId: '',
      form: {
        type: 'PKW',
        fin: '',
        licensePlate: '',
        initialRegistration: '',
        lastName: '',
        firstName: '',
        country: '',
        brand: '',
        model: '',
        category: '',
        engine: '',
        fourWheelDrive: false,
      },
      error: {
        fin: false,
      },
      isFormValid: false,
      showForm: false,
      errorIds: {
        brandSelect: 'brand-error',
        modelSelect: 'model-error',
        categorySelect: 'category-error',
        engineSelect: 'engine-error',
      },
    }
  },
  mounted() {
    this.resetBrands()
    this.getInitialRegistrationText() // preload 3-2-1 text

    this.$nextTick(() => {
      const requiredFields = ['brandSelect', 'modelSelect', 'categorySelect', 'engineSelect']

      requiredFields.forEach((refName) => {
        this.setRequiredAriaAttribut(refName)
      })
    })
  },
  activated() {
    if (this.userVehicles) {
      this.populateDataFromStore()
    } else {
      this.getUserData()
      this.$store.dispatch('getDropdownLists')
    }

    if (this.newVehicle) {
      this.showForm = true

      const model = this.newVehicle.model
      setTimeout(() => {
        this.form.model = model
      }, 200)
    }

    // User lands on the second step of the TVG 'Fahrzeug'
    trackGa4Event({
      ga4Event: 'tvg_step_2',
      ga4ParameterName: 'service',
      ga4ParameterValue: this.selectedServicesName.join(),
    })
  },
  computed: {
    ...mapGetters([
      'sessionData',
      'user',
      'userVehicles',
      'selectedVehicle',
      'newVehicle',
      'rebookVehicle',
      'kfzartId',
      'services',
      'selectedServicesName',
      'selectedServicesId',
      'initialRegistrationText',
      'finInfo',
      'countries',
      'vehicleBrandsModels',
      'vehicleDrives',
      'vehicleCategories',
    ]),
    hasCompletedForm() {
      return Boolean(this.form.brand && this.form.model && this.form.category && this.form.engine)
    },
    hasUserVehicles() {
      return this.userVehicles?.length > 0
    },
    hasNoUserVehicles() {
      return this.userVehicles?.length === 0
    },
    bookableKfzartId() {
      const combinedServices = this.services?.ausgewaehlterLandesclub?.items
        ? this.services.oesterreichweit.items.concat(this.services.ausgewaehlterLandesclub.items)
        : this.services.oesterreichweit.items
      const selectedServices = combinedServices.filter((obj) =>
        this.selectedServicesId.includes(obj.snrId)
      )

      if (selectedServices.length === 1) {
        const availableKfzartId = []
        selectedServices[0].dienstleistungsangebotModelList.items.forEach((item) =>
          availableKfzartId.push(...item.kfzartList)
        )

        return removeDuplicates(availableKfzartId)
      } else {
        const availableKfzartId1 = []
        selectedServices[0]?.dienstleistungsangebotModelList.items.forEach((item) =>
          availableKfzartId1.push(...item.kfzartList)
        )

        const availableKfzartId2 = []
        selectedServices[1]?.dienstleistungsangebotModelList.items.forEach((item) =>
          availableKfzartId2.push(...item.kfzartList)
        )

        return removeDuplicates(availableKfzartId1).filter((kfzartId) =>
          availableKfzartId2.includes(kfzartId)
        )
      }
    },
    kaufException() {
      // Kauf-Überprüfung: -26
      // Kauf plus Fahrwerksüberprüfung: -11
      // Kauf plus 57a-Begutachtung: 988
      return this.selectedServicesId.some((id) => [-26, -11, 988].includes(id))
    },
    isBicycle() {
      return this.form.type === 'Fahrrad'
    },
    filteredVehicleDrives() {
      if (this.isBicycle) {
        return this.vehicleDrives.filter((drive) => drive === 'Keine')
      }
      return this.vehicleDrives
    },
    filteredCategories() {
      switch (this.form.type) {
        case 'PKW': {
          return this.vehicleCategories['Pkw']
        }
        case 'LKW': {
          return this.vehicleCategories['Lkw']
        }
        case 'Motorrad': {
          return this.vehicleCategories['Motorrad']
        }
        case 'Fahrrad': {
          // Fahrrad ist unter "Fahrzeuge ohne Zulassung" eingeordnet
          return (
            this.vehicleCategories['Fahrzeuge ohne Zulassung']?.filter(
              (c) => c.title === 'Fahrrad'
            ) || []
          )
        }
        default: {
          // eslint-disable-next-line
          const { Pkw, Lkw, Motorrad, ...remaining } = this.vehicleCategories
          // Fahrrad aus "Weitere" entfernen, da es einen eigenen Typ hat
          return Object.values(remaining)
            .flat()
            .filter((c) => c.title !== 'Fahrrad')
        }
      }
    },
    showInitialRegistrationText() {
      // If the user has chosen "57a Begutachtung" as a service AND Erstzulassung is less than 3 years ago, we need to display an info text
      const selectedVehicleInitialReg = this.userVehicles?.find(
        (vehicle) => vehicle.vehicle.id === this.selectedVehicleId
      )?.vehicle.erstzulassung

      const initialRegistration =
        selectedVehicleInitialReg || this.form.initialRegistration?.split('.').reverse().join('-') // ISO format yyyy-mm-dd
      const initialRegistrationWithin3Years =
        new Date(initialRegistration) > getDateYearsAgoToday(3) &&
        new Date(initialRegistration) <= new Date()

      return this.selectedServicesId.includes(-1) && initialRegistrationWithin3Years
    },
  },
  watch: {
    userVehicles() {
      if (this.userVehicles) this.populateDataFromStore()
    },
    hasNoUserVehicles() {
      this.showForm = true
    },
    selectedVehicleId() {
      // close form if user selects radio button, while leaving fields intact
      if (this.selectedVehicleId && this.showForm) this.showForm = false
    },
    showForm() {
      if (this.showForm) {
        this.form.firstName = this.user.firstname
        this.form.lastName = this.user.lastname
      }
    },
    form: {
      deep: true,
      handler() {
        this.$store.commit('setNewVehicle', {
          ...this.form,
          licensePlate: this.form.licensePlate?.replace(/ /g, ''),
          categoryKey: this.filteredCategories?.find(
            (category) => category.title === this.form.category
          )?.id,
        })

        if (this.selectedVehicle) {
          this.$store.commit('setSelectedVehicle', null) // reset
        }

        if (this.hasCompletedForm) {
          this.$refs.elForm.validate((isValid) => {
            this.isFormValid = isValid
          })
        }
      },
    },
    'form.brand'() {
      // clear linked field
      this.form.model = ''
    },
    'form.type'(newType) {
      // clear linked field
      this.form.category = ''
      this.form.fourWheelDrive = false

      // Fahrrad-Defaults setzen
      if (newType === 'Fahrrad') {
        this.form.brand = 'Sonstige'
        this.form.fin = ''
        this.form.licensePlate = ''
        this.form.initialRegistration = ''
        this.form.engine = 'Keine'

        // Model, Kategorie nach nextTick setzen, da der brand-Watcher model leert
        this.$nextTick(() => {
          this.form.model = 'Sonstige'

          const fahrradCategory = this.filteredCategories?.find((c) => c.title === 'Fahrrad')
          if (fahrradCategory) {
            this.form.category = fahrradCategory.title
          }
        })
      }
    },
    finInfo() {
      if (!this.finInfo.valid) {
        this.error.fin = 'Ungültige Eingabe'
      } else if (this.finInfo.data) {
        this.error.fin = ''
        this.form.brand = this.finInfo.data.brand
      }
    },
    kfzartId() {
      // populated after getNewVehicleKfzartId
      if (this.kfzartId && this.showForm) {
        this.nextStep()
      }
    },
  },
  methods: {
    getUserData() {
      if (this.user?.isMember) {
        this.$store.dispatch('getUserVehicles', this.user.pnr)
      } else {
        this.$store.commit('setUserVehicles', [])
      }
    },
    getInitialRegistrationText() {
      if (this.selectedServicesId.includes(-1)) this.$store.dispatch('getInitialRegistrationText')
    },
    populateDataFromStore() {
      if (this.rebookVehicle) {
        const rebookVehicleInMOE = this.userVehicles.find(
          (vehicle) =>
            vehicle.vehicle.fin === this.rebookVehicle.fin &&
            vehicle.vehicle.licensePlate === this.rebookVehicle.licensePlate
        )

        if (rebookVehicleInMOE) {
          this.selectedVehicleId = rebookVehicleInMOE.vehicle.id
          this.$store.commit(
            'setSelectedVehicle',
            this.userVehicles.find((vehicle) => vehicle.vehicle.id === this.selectedVehicleId)
              .vehicle
          )
        } else {
          this.$store.commit('setNewVehicle', {
            type: 'PKW',
            fin: this.rebookVehicle.fin,
            licensePlate: this.rebookVehicle.licensePlate,
            initialRegistration: '',
            lastName: this.rebookVehicle.lastName,
            firstName: this.rebookVehicle.firstName,
            country: '',
            brand: this.rebookVehicle.brand,
            model: this.rebookVehicle.model,
            category: '',
            engine: this.rebookVehicle.diesel ? 'Diesel' : '',
            fourWheelDrive: this.rebookVehicle.fourWheelDrive,
          })
          this.showForm = true
        }
      }

      if (this.selectedVehicle) {
        this.selectedVehicleId = this.selectedVehicle.id
      }

      if (this.newVehicle) {
        this.form = this.newVehicle

        // model was cleared after brand was set
        setTimeout(() => {
          this.form.model = this.rebookVehicle?.model
        }, 200)

        // not applicable to Kauf-Uberprufung
        this.$refs.elFormFin?.focus()
        setTimeout(() => {
          this.$refs.elFormFin?.blur()
        }, 200)
      }
    },
    isMotorrad(category) {
      return (
        'Motorrad' ===
        Object.keys(this.vehicleCategories).find((key) =>
          this.vehicleCategories[key].map((el) => el.title).includes(category)
        )
      )
    },
    isUserVehicleDisabled(kfzartId) {
      return !this.bookableKfzartId.includes(kfzartId)
    },
    handleSelectBlur(field, refName) {
      setTimeout(() => {
        this.$refs.elForm
          .validateField(field)
          .then((isValid) => {
            if (isValid) this.updateAriaAttributes(refName, true)
            else this.updateAriaAttributes(refName, false)
          })
          .catch(() => {
            this.updateAriaAttributes(refName, false)
          })
      }, 200) // otherwise the error message will pop in and out
    },
    clickVehicleHandler(id, kfzartId) {
      if (this.isUserVehicleDisabled(kfzartId)) return

      if (this.selectedVehicleId === id) {
        this.selectedVehicleId = '' // cancel radio button if reclicking
      } else {
        this.selectedVehicleId = id
      }

      this.$store.commit('setNewVehicle', null) // reset
      this.$store.commit(
        'setSelectedVehicle',
        this.userVehicles.find((vehicle) => vehicle.vehicle.id === this.selectedVehicleId)?.vehicle
      )
    },
    handleVehicleSelectChange(id) {
      this.$store.commit('setNewVehicle', null) // reset
      this.$store.commit(
        'setSelectedVehicle',
        this.userVehicles.find((vehicle) => vehicle.vehicle.id === id).vehicle
      )
    },
    validateFin() {
      if (this.form.fin) {
        const fin = this.form.fin.replaceAll('_', '').replaceAll(' ', '').toUpperCase()
        this.form.fin = fin
        this.error.fin = ''
        this.$refs.elForm.validateField('fin', (isValid) => {
          if (isValid && this.form.fin !== '') this.$store.dispatch('getFinInformation', fin)
        })
      }
    },
    toggleShowForm() {
      this.showForm = !this.showForm
      if (this.showForm) {
        this.selectedVehicleId = '' // clear radio buttons when opening form
      } else {
        this.$refs.elForm.resetFields()
      }
    },
    filterBrands(value) {
      if (value.length === 1) {
        this.filteredBrands = Object.keys(this.vehicleBrandsModels).filter((option) =>
          option.toLowerCase().startsWith(value.toLowerCase())
        )
      } else {
        this.filteredBrands = Object.keys(this.vehicleBrandsModels).filter((option) =>
          option.toLowerCase().includes(value.toLowerCase())
        )
      }
    },
    resetBrands() {
      this.filteredBrands = Object.keys(this.vehicleBrandsModels)
    },
    nextStep() {
      if (!this.kfzartId) {
        this.$store.dispatch('getNewVehicleKfzartId', {
          fourWheelDrive: this.newVehicle.fourWheelDrive,
          engine: this.newVehicle.engine,
          categoryKey: this.newVehicle.categoryKey,
        })
        this.trackCreateNewVehicle()
        // discontinue while waiting for API
        return
      }

      this.$store.commit('setActiveStep', 3)
      this.$store.commit('clearTelephoneStationIds')
      this.$store.commit('clearKeineTermineStationIds')
      this.$store.commit('clearUnbookableStationIds')
    },
    trackCreateNewVehicle() {
      let vehicleType
      if (this.form.type === 'PKW') vehicleType = 'pkw'
      if (this.form.type === 'Motorrad') vehicleType = 'motor_cycle'
      if (this.form.type === 'Fahrrad') vehicleType = 'bicycle'
      if (this.form.type === 'LKW') vehicleType = 'lkw'
      if (this.form.type === 'Weitere') vehicleType = 'other'

      // User creates a new vehicle successfully
      trackGa4Event({
        ga4Event: 'tvg_new_vehicle',
        ga4ParameterName: 'vehicle_type',
        ga4ParameterValue: vehicleType,
      })
    },
    selectChangeHandler(refName) {
      this.$refs[refName].blur()
    },
    setRequiredAriaAttribut(refName) {
      const selectRef = this.$refs[refName].$el
      if (!selectRef) return
      const input = selectRef.querySelector('.el-select__input')
      if (!input) return

      input.setAttribute('aria-required', 'true')
    },
    updateAriaAttributes(refName, isValid) {
      const selectRef = this.$refs[refName].$el
      if (!selectRef) return
      const input = selectRef.querySelector('.el-select__input')
      if (!input) return

      const errorId = this.errorIds[refName]
      console.log('errorId', errorId)

      if (isValid) {
        input.removeAttribute('aria-invalid')
        input.removeAttribute('aria-describedby', errorId)
      } else {
        input.setAttribute('aria-invalid', 'true')
        input.setAttribute('aria-describedby', errorId)
      }
    },
  },
}
</script>

<style lang="scss">
.vehicle {
  h4 {
    margin-bottom: 16px;
  }

  > hr {
    @include container-divider;
  }

  &__new {
    margin-bottom: 40px;
  }

  &__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;

    #content &__subheadline,
    &__subheadline {
      h2 {
        font-weight: $font-weight-medium;
        margin-bottom: 4px;
        font-size: 16px;
        line-height: $line-height;
      }
      p {
        color: $color-text-secondary;
      }
    }

    &__action {
      @include flex-center-align;
      gap: 4px;
    }
  }

  &__form {
    .el-form--inline {
      gap: 18px 24px;
    }

    .el-form-item {
      position: relative;
      margin: 0 !important;
      width: 100%;
      max-width: 344px;

      @media (max-width: $breakpoint-max-mobile) {
        max-width: none;
      }

      &__content {
        position: static !important;

        .el-checkbox {
          margin: 0;
          padding: 15px 0;

          &:hover {
            background-color: unset;
          }
        }
      }

      &__label {
        display: flex !important;
        justify-content: flex-start;
        padding: 0 0 2px 0 !important;

        &__info {
          cursor: pointer;
          margin-left: auto;
          height: 24px;
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &::before {
          content: '' !important;
        }

        .required {
          color: #4a4a52;
          font-weight: $font-weight-medium;
          font-size: 12px;
          margin-left: 4px;
        }
      }

      &__error {
        position: static;
      }
    }

    .el-radio-group {
      flex: 100%;

      .el-radio-button {
        margin: 32px 0 12px;

        &__original-radio:not(:checked) + .el-radio-button__inner {
          &:hover {
            background-color: $color-grey-light;
            color: initial;
          }
        }
      }
    }

    .row {
      flex: 100%;
      margin-bottom: 12px;

      .message {
        @include flex-center-align;
        color: rgba($color-text-primary, 0.7);
        font-size: 12px;
        line-height: 1;
        margin: 8px 0;

        svg {
          margin-right: 4px;
        }
      }
    }

    .row .is-error + .message {
      display: none; // hide fin message if there is an error
    }

    &__error {
      color: $color-danger-primary;
      font-size: 12px;
      padding-top: 2px;
    }

    .el-dialog__body {
      padding: 0 !important;
    }

    .el-select {
      width: 100%;

      &__popper {
        left: 0 !important;
      }

      // hard-code zIndexes of the select dropdowns because input is on top
      &.z2001 {
        .el-select__wrapper {
          z-index: 2001;
        }
        .el-select__popper {
          z-index: 2000 !important;
        }
      }
      &.z2003 {
        .el-select__wrapper {
          z-index: 2003;
        }
        .el-select__popper {
          z-index: 2002 !important;
        }
      }
      &.z2005 {
        .el-select__wrapper {
          z-index: 2005;
        }
        .el-select__popper {
          z-index: 2004 !important;
        }
      }
      &.z2007 {
        .el-select__wrapper {
          z-index: 2007;
        }
        .el-select__popper {
          z-index: 2006 !important;
        }
      }
      &.z2009 {
        .el-select__wrapper {
          z-index: 2009;
        }
        .el-select__popper {
          z-index: 2008 !important;
        }
      }
    }
  }
}
</style>
